import React from 'react';
import type { MouseEvent } from 'react';
import type { Theme } from '@material-ui/core';
import {
    List,
    ListItem,
    ListItemText,
    Popper,
    Box,
    IconButton,
    ClickAwayListener,
} from '@material-ui/core';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { Block } from '@klp/design';
import { useTranslation } from 'react-i18next';
import { FileDownload } from '@klp/filedownload';
import { contractToRegistrationApiRoutes } from '../config';
import { ContractRegistrationRoutes } from '@forvalter/routing/Routes';
import { useHistory } from 'react-router-dom';
import {
    contractToRegistrationApi,
    RegistrationStatuses,
    useLazyGetContractRegistrationProcessQuery,
    useRegisterContractMutation,
} from '../services/rtkApi';
import { CheckOffContractRequest } from '../classes/CheckOffContractRequest';
import { useDispatch } from 'react-redux';
import type { IContractToRegistration } from '../classes';
import { useKlpStyles } from '@klp/design/Theme/useKlpStyles';
import { showToast } from '@klpkonsern/ui';

type PopperProps = {
    handleDownload: { path?: string; name?: string };
    handleDetails: {
        dokumentId?: string;
        registrationLine: IContractToRegistration;
    };
    handleAssignResponsible: { dokumentId?: string };
    open: boolean;
    anchorEl: HTMLElement | null;
    onToggle: (event: MouseEvent<HTMLButtonElement>) => void;
    onClose: () => void;
};

const styles = (theme: Theme) => ({
    popper: {
        background: theme.palette.background.paper,
        border: '1px solid transparent',
        borderColor: theme.palette.primary.dark,
        borderRadius: theme.shape.borderRadius,
        zIndex: 1000,
    },
    iconContainer: {
        display: 'flex',
        alignItems: 'center',
        paddingRight: theme.spacing(2),
    },
    redText: {
        color: 'red',
    },
    button: {
        cursor: 'pointer',
    },
});

export const MenuPopper: React.FC<PopperProps> = ({
    handleDetails,
    handleDownload,
    handleAssignResponsible,
    open,
    anchorEl,
    onToggle,
    onClose,
}) => {
    const { t } = useTranslation();
    const classes = useKlpStyles({}, styles);

    const history = useHistory();
    const dispatch = useDispatch();
    const [restartProcess] = useLazyGetContractRegistrationProcessQuery();
    const [registerContract] = useRegisterContractMutation();

    const handleToggle = (event: MouseEvent<HTMLButtonElement>) => {
        event.stopPropagation();
        onToggle(event);
    };

    const handleClick =
        (callback: () => Promise<void> | void) =>
        (event: MouseEvent<HTMLElement>) => {
            event.stopPropagation();
            callback();
        };

    const handleDokumentIdSelect = (dokumentId: string) => () => {
        history.push(ContractRegistrationRoutes.single(dokumentId));
    };

    const handleOpenAssignResponsible = (dokumentId: string) => () => {
        history.push(ContractRegistrationRoutes.assignResponsible(dokumentId));
    };

    const handleRestartProcess = (dokumentId: string) => async () => {
        showToast({
            body: t('contractRegistration.notification'),
            type: 'info',
        });

        dispatch(contractToRegistrationApi.util.invalidateTags(['Process']));

        await restartProcess({
            kontraktDokumentId: dokumentId,
            startNewProcess: true,
        });

        showToast({
            body: t('contractRegistration.successfulOperation'),
            type: 'success',
        });

        history.push(ContractRegistrationRoutes.single(dokumentId));
    };

    const handleShareDocument = (dokumentId: string) => () => {
        history.push(ContractRegistrationRoutes.shareDocument(dokumentId));
    };

    const handleNotRelevant = (dokumentId: string) => async () => {
        showToast({
            body: t('contractRegistration.notification'),
            type: 'info',
        });

        const checkOffContract = new CheckOffContractRequest({
            dokumentId,
            kontraktlinjeIds:
                handleDetails.registrationLine.avsjekkKontraktlinjeIds,
            kommentar: '',
            skalRegistreres: false,
        });

        await registerContract({
            ...checkOffContract,
            skipCacheProcessInvalidation: true,
        });

        showToast({
            body: t('contractRegistration.successfulOperation'),
            type: 'success',
        });

        onClose();
    };

    const shareDocumentStatuses = [
        RegistrationStatuses.NY,
        RegistrationStatuses.TIL_QA,
        RegistrationStatuses.FULLFORT,
    ];

    return (
        <ClickAwayListener onClickAway={onClose}>
            <div>
                <IconButton
                    color="inherit"
                    aria-label="Open document view"
                    className={classes.button}
                    onClick={handleToggle}
                >
                    <Illustration icon={IconName.OPTIONS} />
                </IconButton>
                <Popper
                    id={open ? 'transition-popper' : undefined}
                    open={open}
                    anchorEl={anchorEl}
                    placement="bottom-start"
                    transition={true}
                    className={classes.popper}
                >
                    <Box>
                        <List>
                            <ListItem
                                button={true}
                                dense={true}
                                onClick={handleClick(
                                    handleDokumentIdSelect(
                                        handleDetails.dokumentId ?? '',
                                    ),
                                )}
                            >
                                <Block className={classes.iconContainer}>
                                    <Illustration icon={IconName.CIRCLE_INFO} />
                                </Block>
                                <ListItemText>
                                    {t(
                                        'contractRegistration.table.actionMenuItems.showDetails',
                                    )}
                                </ListItemText>
                            </ListItem>

                            {handleDetails.registrationLine
                                ?.registreringStatus ===
                                RegistrationStatuses.TILDELT_OKONOMI && (
                                <ListItem
                                    button={true}
                                    dense={true}
                                    onClick={handleClick(
                                        handleOpenAssignResponsible(
                                            handleAssignResponsible.dokumentId ??
                                                '',
                                        ),
                                    )}
                                >
                                    <Block className={classes.iconContainer}>
                                        <Illustration icon={IconName.PROFILE} />
                                    </Block>
                                    <ListItemText>
                                        {t(
                                            'contractRegistration.table.actionMenuItems.assignFinancePerson',
                                        )}
                                    </ListItemText>
                                </ListItem>
                            )}

                            <FileDownload
                                path={
                                    handleDownload?.path &&
                                    contractToRegistrationApiRoutes.download(
                                        handleDownload.path,
                                    )
                                }
                                name={`${handleDownload?.name}`}
                                stopTableClickPropagation={true}
                            >
                                <ListItem button={true} dense={true}>
                                    <Block className={classes.iconContainer}>
                                        <Illustration
                                            icon={IconName.DOWNLOAD}
                                        />
                                    </Block>
                                    <ListItemText>
                                        {t(
                                            'contractRegistration.table.actionMenuItems.download',
                                        )}
                                    </ListItemText>
                                </ListItem>
                            </FileDownload>

                            {shareDocumentStatuses.includes(
                                handleDetails.registrationLine
                                    ?.registreringStatus as RegistrationStatuses,
                            ) && (
                                <ListItem
                                    button={true}
                                    dense={true}
                                    divider={true}
                                    onClick={handleClick(
                                        handleShareDocument(
                                            handleDetails.dokumentId ?? '',
                                        ),
                                    )}
                                >
                                    <Block className={classes.iconContainer}>
                                        <Illustration
                                            icon={IconName.DOC_OUTBOUND}
                                        />
                                    </Block>
                                    <ListItemText>
                                        {t(
                                            handleDetails.registrationLine
                                                ?.registreringStatus ===
                                                RegistrationStatuses.FULLFORT
                                                ? 'checkOffContract.shareButtonCompleted'
                                                : 'checkOffContract.shareButton',
                                        )}
                                    </ListItemText>
                                </ListItem>
                            )}

                            <ListItem
                                button={true}
                                dense={true}
                                onClick={handleClick(
                                    handleRestartProcess(
                                        handleDetails.dokumentId ?? '',
                                    ),
                                )}
                            >
                                <Block className={classes.iconContainer}>
                                    <Illustration icon={IconName.CHANGE} />
                                </Block>
                                <ListItemText>
                                    {t(
                                        'contractRegistration.table.actionMenuItems.restartProcess',
                                    )}
                                </ListItemText>
                            </ListItem>

                            <ListItem
                                button={true}
                                dense={true}
                                onClick={handleClick(
                                    handleNotRelevant(
                                        handleDetails.dokumentId ?? '',
                                    ),
                                )}
                            >
                                <Block className={classes.iconContainer}>
                                    <Illustration icon={IconName.ALERT} />
                                </Block>
                                <ListItemText className={classes.redText}>
                                    {t(
                                        'contractRegistration.table.actionMenuItems.notRelevant',
                                    )}
                                </ListItemText>
                            </ListItem>
                        </List>
                    </Box>
                </Popper>
            </div>
        </ClickAwayListener>
    );
};
